import type { ComponentType } from "react";

/**
 * - `drawer`: no permanent navigation; the header's menu button opens an overlay drawer.
 * - `wide`: the permanent expanded column (kept for a route that opts in).
 * - `compact`: the permanent icon rail.
 * - `hidden`: no navigation.
 */
export type PrimaryNavigationMode = "drawer" | "wide" | "compact" | "hidden";

/**
 * Controls only the outer page treatment. Feature-owned panels remain
 * responsible for their own internal padding and scrolling.
 */
export type MainContentMode = "centered" | "padded" | "fullBleed";

export interface RouteChromeConfig {
  /** Static title displayed beside the MagicBI brand in the global header. */
  headerTitle?: string;
  /** Primary application navigation presentation for the matching route. */
  primaryNavigation?: PrimaryNavigationMode;
  /** Width, gutter, and overflow treatment applied around route content. */
  mainContent?: MainContentMode;
}

/**
 * What a single route can configure for the shell.
 * `Secondary` / `Breadcrumb` are component types (not JSX) so each route's
 * component can run its own hooks (useLocation, useNavigate, etc.) when rendered.
 */
export interface LayoutSlotConfig {
  Secondary?: ComponentType;
  Breadcrumb?: ComponentType;
  /** Global page chrome resolved alongside optional shell slots. */
  chrome?: RouteChromeConfig;
  /**
   * Width of the secondary sidebar column. CSS length (`"20%"`, `"220px"`).
   * Defaults to `"auto"` so the sidebar sizes to its content — set explicitly
   * only when the secondary component uses `width: 100%` to fill its cell.
   */
  secondaryWidth?: string;
}

export interface RouteLayoutEntry {
  /** Returns true when this entry should apply to the current pathname. */
  match: (pathname: string) => boolean;
  /** Slot components for this route. */
  config: LayoutSlotConfig;
}

/**
 * Add a new entry to extend layouts. No central conditional grows — the
 * resolver walks this array in order and returns the first match.
 */
export const LAYOUT_REGISTRY: RouteLayoutEntry[] = [];

/** Append a new route → layout mapping. Call this once at module import time. */
export const registerLayout = (entry: RouteLayoutEntry): void => {
  LAYOUT_REGISTRY.push(entry);
};

/** Look up the slot config for a pathname. Returns empty config if no match. */
export const resolveLayout = (pathname: string): LayoutSlotConfig => {
  const entry = LAYOUT_REGISTRY.find(({ match }) => match(pathname));
  return entry?.config ?? {};
};
